/**
 * NEXUS UI - Timeline
 * Vertical timeline with animated markers
 */

import React from 'react';
import type { BaseProps, NexusVariant, TimelineEvent } from './types';

export interface TimelineProps extends BaseProps {
  events: TimelineEvent[];
  variant?: NexusVariant;
  animated?: boolean;
}

const variantColors: Record<NexusVariant, { primary: string; glow: string }> = {
  cyan: { primary: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { primary: '#3b82f6', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { primary: '#a855f7', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { primary: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { primary: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { primary: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)' },
};

export const Timeline: React.FC<TimelineProps> = ({
  events,
  variant = 'cyan',
  animated = true,
  className = '',
  style,
}) => {
  const colors = variantColors[variant];

  return (
    <div
      className={`nx-timeline ${className}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}
    >
      {events.map((event, index) => (
        <div
          key={event.id}
          className={`nx-timeline__item ${event.active ? 'nx-timeline__item--active' : ''}`}
          style={{
            display: 'flex',
            gap: '16px',
            animation: animated ? `nx-pulse 0.5s ease-out ${index * 0.1}s both` : 'none',
          }}
        >
          {/* Track column (marker + line) */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {/* Marker */}
            <div
              className="nx-timeline__marker"
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: event.active ? colors.primary : 'var(--nx-void-panel)',
                border: `2px solid ${colors.primary}`,
                boxShadow: event.active ? `0 0 12px ${colors.glow}` : 'none',
                transition: 'all 0.3s ease-out',
                flexShrink: 0,
                position: 'relative',
              }}
            >
              {/* Inner dot for active state */}
              {event.active && (
                <div
                  style={{
                    position: 'absolute',
                    inset: '3px',
                    borderRadius: '50%',
                    background: colors.primary,
                  }}
                />
              )}
            </div>

            {/* Connecting line segment */}
            {index < events.length - 1 && (
              <div
                style={{
                  width: '2px',
                  flex: 1,
                  minHeight: '24px',
                  background: colors.primary,
                  opacity: 0.4,
                }}
              />
            )}
          </div>

          {/* Content column */}
          <div style={{ flex: 1, paddingBottom: index < events.length - 1 ? '16px' : 0 }}>
            {/* Time */}
            <div
              className="nx-timeline__time"
              style={{
                fontFamily: 'var(--nx-font-mono)',
                fontSize: '11px',
                color: 'var(--nx-text-ghost)',
                marginBottom: '4px',
              }}
            >
              {event.time}
            </div>

            {/* Title */}
            <div
              className="nx-timeline__title"
              style={{
                fontFamily: 'var(--nx-font-interface)',
                fontSize: '14px',
                fontWeight: 600,
                color: event.active ? colors.primary : 'var(--nx-text-primary)',
                marginBottom: event.description ? '4px' : 0,
                transition: 'color 0.3s ease-out',
              }}
            >
              {event.title}
            </div>

            {/* Description */}
            {event.description && (
              <div
                className="nx-timeline__description"
                style={{
                  fontFamily: 'var(--nx-font-interface)',
                  fontSize: '14px',
                  color: 'var(--nx-text-secondary)',
                  lineHeight: 1.5,
                }}
              >
                {event.description}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Timeline;
